<template>
  <div class="agora-inline">
    <div class="agora-inline__header">
      <span class="agora-inline__title">{{ displayTitle }}</span>
      <span class="agora-inline__status">{{ liveState && videoReady ? '直播中' : statusText }}</span>
    </div>

    <div
      ref="playerWrapRef"
      class="agora-inline__player-wrap"
      :class="{ 'agora-inline__player-wrap--fullscreen': fullscreenActive }"
      @dblclick="pickAimPoint"
      @pointerdown.capture="handleRightPointerDown"
      @pointermove.capture="handleRightPointerMove"
      @pointerup.capture="handleRightPointerUp"
      @pointercancel.capture="cancelRightPointerSelection"
      @contextmenu.capture="handlePlayerContextMenu"
    >
      <div ref="playerRef" class="agora-inline__player"></div>
      <slot
        name="overlay"
        :video-id="activeVideoId"
        :camera-index="cameraSelected || ''"
        :lens="lensSelected || ''"
        :video-ready="videoReady"
        :video-rect="videoDisplayRect"
        :latency-ms="latencyMs"
      ></slot>
      <div
        v-if="rightSelectionDraftStyle"
        class="agora-inline__right-selection"
        :style="rightSelectionDraftStyle"
      ></div>
      <button
        v-if="aimActive"
        type="button"
        class="agora-inline__aim-picker"
        aria-label="点击画面选择瞄准目标"
        @click="pickAimPoint"
      >
        <span class="agora-inline__aim-hint">点击画面目标点下发云台瞄准</span>
      </button>
      <div v-if="!videoReady" class="agora-inline__placeholder">
        {{ statusText }}
      </div>
      <button
        v-if="videoReady"
        type="button"
        class="agora-inline__fullscreen"
        :title="fullscreenActive ? '退出全屏' : '全屏'"
        :aria-label="fullscreenActive ? '退出全屏' : '全屏'"
        @click="toggleFullscreen"
      >
        <FullScreenTwo v-if="fullscreenActive" />
        <FullScreen v-else />
      </button>
      <div v-if="videoReady" class="agora-inline__latency">
        {{ latencyText }}
      </div>
    </div>

    <div v-if="sourceList.length > 0" class="agora-inline__controls">
      <div v-if="cameraList.length > 1" class="agora-inline__group" role="radiogroup" aria-label="相机选择">
        <span class="agora-inline__group-label" aria-hidden="true"><Camera /></span>
        <template v-if="cameraList.length <= 3">
          <button
            v-for="item in cameraList"
            :key="item.value"
            type="button"
            role="radio"
            class="agora-inline__chip"
            :class="{ 'agora-inline__chip--active': cameraSelected === item.value }"
            :aria-checked="cameraSelected === item.value"
            :disabled="loading"
            :title="item.label"
            @click="selectCameraByValue(item.value)"
          >
            {{ cameraChipLabel(item) }}
          </button>
        </template>
        <a-select
          v-else
          v-model:value="cameraSelected"
          size="small"
          class="agora-inline__select"
          placeholder="相机"
          dropdown-class-name="agora-inline-dropdown"
          :disabled="loading"
          @change="onCameraChange"
        >
          <a-select-option v-for="item in cameraList" :key="item.value" :value="item.value">
            {{ item.label }}
          </a-select-option>
        </a-select>
      </div>
      <a-select
        v-model:value="claritySelected"
        size="small"
        class="agora-inline__select agora-inline__select--clarity"
        placeholder="清晰度"
        dropdown-class-name="agora-inline-dropdown"
        :disabled="loading || !liveState"
        @change="updateQuality"
      >
        <a-select-option v-for="item in clarityList" :key="item.value" :value="item.value">
          {{ item.label }}
        </a-select-option>
      </a-select>
      <slot name="actions" :video-ready="videoReady" :live-state="liveState" :lens="lensSelected || ''"></slot>
      <span class="agora-inline__divider" aria-hidden="true"></span>
      <button type="button" class="agora-inline__btn" :disabled="loading || cameraList.length === 0" title="刷新直播" @click="restartLive">
        <Loading v-if="loading" spin />
        <Refresh v-else />
        <span class="agora-inline__btn-text">刷新</span>
      </button>
      <button type="button" class="agora-inline__btn agora-inline__btn--stop" :disabled="loading || !stoppableVideoId" title="停止直播" @click="stopCurrentLive">
        <span class="agora-inline__stop-mark" aria-hidden="true"></span>
        <span class="agora-inline__btn-text">停止</span>
      </button>
      <button
        v-if="videoReady"
        type="button"
        class="agora-inline__btn"
        :class="{ 'agora-inline__btn--relay-active': aliyunRelayActive }"
        :disabled="loading || aliyunRelayLoading"
        :title="aliyunRelayActive ? '停止推送阿里云直播' : '推送阿里云直播'"
        @click="toggleAliyunRelay"
      >
        <Loading v-if="aliyunRelayLoading" spin />
        <Broadcast v-else />
        <span class="agora-inline__btn-text">{{ aliyunRelayActive ? '停止推送' : '推送阿里云' }}</span>
      </button>
      <button
        v-if="videoReady && aliyunRelayCanPlay"
        type="button"
        class="agora-inline__btn"
        :disabled="aliyunRelayLoading"
        title="播放阿里云直播"
        @click="showAliyunPlayer"
      >
        <Play />
        <span class="agora-inline__btn-text">播放阿里云</span>
      </button>
      <span v-if="aliyunRelayStatusText" class="agora-inline__relay-status">
        {{ aliyunRelayStatusText }}
      </span>
    </div>

    <div v-if="lensList.length > 0" class="agora-inline__lenses">
      <span class="agora-inline__label">镜头</span>
      <div class="agora-inline__group" role="radiogroup" aria-label="镜头选择">
        <button
          v-for="lens in lensList"
          :key="lens"
          type="button"
          role="radio"
          class="agora-inline__chip"
          :class="{ 'agora-inline__chip--active': lensSelected === lens }"
          :aria-checked="lensSelected === lens"
          :disabled="loading || !liveState || !!lensSwitching"
          :title="lens"
          @click="selectLens(lens)"
        >
          <Loading v-if="lensSwitching === lens" spin />
          <span>{{ lensLabel(lens) }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
/* global defineProps, defineEmits, defineExpose */
import AgoraRTC, { IAgoraRTCClient, IAgoraRTCRemoteUser, IRemoteVideoTrack } from 'agora-rtc-sdk-ng'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { message } from 'ant-design-vue'
import Broadcast from '@icon-park/vue-next/es/icons/Broadcast'
import Camera from '@icon-park/vue-next/es/icons/Camera'
import FullScreen from '@icon-park/vue-next/es/icons/FullScreen'
import FullScreenTwo from '@icon-park/vue-next/es/icons/FullScreenTwo'
import Loading from '@icon-park/vue-next/es/icons/Loading'
import Play from '@icon-park/vue-next/es/icons/Play'
import Refresh from '@icon-park/vue-next/es/icons/Refresh'
import type { LiveContextPoint } from '/@/types/live-stream'
import { VideoDisplayRect, calculateCoverRect } from '/@/utils/poi-projection'
import type { PayloadInteractionRect } from '/@/utils/payload-video-interaction'
import { normalizeVideoRect } from '/@/utils/payload-video-interaction'
import { isRightBoxDrag } from '/@/utils/dji-ai-recognition'
import { useAliyunLiveRelay } from '/@/components/aliyun-live/use-aliyun-live-relay'
import {
  changeLivestreamLens,
  getLiveCapacity,
  refreshAgoraToken,
  setLivestreamQuality,
  startLivestream,
  stopLivestream
} from '/@/api/manage'

type LiveSourceType = 'dock' | 'drone'

interface SelectOption {
  value: string,
  label: string,
  more?: any[]
}

interface Props {
  targetSn: string,
  gatewaySn?: string,
  sourceType?: LiveSourceType,
  preferredCamera?: 'main' | 'auxiliary' | string,
  drcActive?: boolean,
  aimActive?: boolean,
  contextPointEnabled?: boolean,
  codec?: 'vp8' | 'h264',
  rightBoxSelectionEnabled?: boolean,
  closeOnStop?: boolean,
  /**
   * When set, the panel does not look for a source at all and shows this text
   * instead (e.g. the aircraft is neither in the dock nor powered on).
   */
  unavailableText?: string,
  title: string,
  active: boolean
}

const props = defineProps<Props>()
const emit = defineEmits([
  'live-state-change',
  'lens-state-change',
  'aim-point',
  'context-point',
  'sei-data',
  'right-box-select',
  'right-box-selection-invalid',
  'request-close',
])

const clarityList = [
  { value: 0, label: 'Adaptive' },
  { value: 1, label: 'Smooth' },
  { value: 2, label: 'Standard' },
  { value: 3, label: 'HD' },
  { value: 4, label: 'Super Clear' }
]
const LIVE_ALREADY_STARTED_CODE = 13003
const DRONE_CAPACITY_RETRY_COUNT = 20
const DRONE_CAPACITY_RETRY_INTERVAL = 1000
const DRONE_MAIN_CAMERA_INDEXES = ['98-0-0', '99-0-0']
const DRONE_AUXILIARY_CAMERA_INDEX = '176-0-0'
const DRONE_LENS_TYPES = ['wide', 'zoom', 'ir']

const playerWrapRef = ref<HTMLElement>()
const playerRef = ref<HTMLElement>()
const loading = ref(false)
const statusText = ref('等待直播启动')
const sourceList = ref<SelectOption[]>([])
const cameraList = ref<SelectOption[]>([])
const videoList = ref<SelectOption[]>([])
const lensList = ref<string[]>([])
const sourceSelected = ref<string>()
const cameraSelected = ref<string>()
const videoSelected = ref<string>()
const lensSelected = ref<string>()
const claritySelected = ref(0)
const liveState = ref(false)
const videoReady = ref(false)
const activeVideoId = ref('')
const latencyMs = ref<number | null>(null)
const fullscreenActive = ref(false)
const videoDisplayRect = ref<VideoDisplayRect>(calculateCoverRect(0, 0, 16, 9))

let agoraClient: IAgoraRTCClient | null = null
let activeVideoTrack: IRemoteVideoTrack | null = null
let activeVideoUid: IAgoraRTCRemoteUser['uid'] | null = null
let latencyTimer: number | undefined
let fallbackFullscreen = false
let runVersion = 0
let stopLivePromise: Promise<void> | null = null
let lastStoppedVideoId = ''
let playerResizeObserver: ResizeObserver | null = null
let activeVideoElement: HTMLVideoElement | null = null
let activeSeiTrack: IRemoteVideoTrack | null = null
let suppressContextMenuTimer: number | undefined

const {
  active: aliyunRelayActive,
  loading: aliyunRelayLoading,
  canPlay: aliyunRelayCanPlay,
  toggle: toggleAliyunRelayInternal,
  showPlayer: showAliyunPlayer,
  restoreIfNeeded: restoreAliyunRelay,
  stopForSourceShutdown: stopAliyunRelayForSourceShutdown,
  statusText: aliyunRelayStatusText,
} = useAliyunLiveRelay({
  getVideoId: () => activeVideoId.value,
  getRemoteVideoUid: () => activeVideoUid,
})

interface RightPointerSelection {
  pointerId: number;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  dragging: boolean;
}

const rightPointerSelection = ref<RightPointerSelection | null>(null)

const selectedSourceName = computed(() => {
  return sourceList.value.find(item => item.value === sourceSelected.value)?.label || ''
})

const displayTitle = computed(() => {
  const title = props.title.trim()
  if (selectedSourceName.value) {
    return selectedSourceName.value
  }
  if (title && title !== '机场直播' && title !== '无人机直播') {
    return title
  }
  return props.targetSn || title
})

const lensSwitching = ref('')
const LENS_LABELS: Record<string, string> = {
  wide: '广角',
  zoom: '变焦',
  ir: '红外',
  normal: '标准',
}

function lensLabel (lens: string) {
  return LENS_LABELS[lens] || lens
}

/** Short chip text; the full camera name stays available as the tooltip. */
function cameraChipLabel (item: SelectOption) {
  if (props.sourceType === 'drone') {
    if (DRONE_MAIN_CAMERA_INDEXES.includes(item.value)) {
      return '主相机'
    }
    if (item.value === DRONE_AUXILIARY_CAMERA_INDEX) {
      return '辅助相机'
    }
  }
  return item.label.replace(/\s*camera\s*/i, ' ').trim() || item.label
}

const latencyText = computed(() => {
  return latencyMs.value === null ? '--ms' : `${latencyMs.value}ms`
})

const stoppableVideoId = computed(() => {
  return activeVideoId.value || buildSelectedVideoId()
})

const rightSelectionDraftStyle = computed(() => {
  const selection = rightPointerSelection.value
  if (!selection?.dragging) {
    return null
  }
  const draft = normalizeVideoRect(
    selection.startX,
    selection.startY,
    selection.currentX,
    selection.currentY,
    videoDisplayRect.value,
    0
  )
  if (!draft) {
    return null
  }
  return normalizedRectStyle(draft)
})

watch(
  () => [props.active, props.targetSn, props.gatewaySn, props.sourceType, props.preferredCamera, props.drcActive, props.codec, props.unavailableText || ''],
  async ([active]) => {
    const version = nextRunVersion()
    if (active && props.targetSn && !props.unavailableText) {
      await openPanel(version)
      return
    }
    // stopLive() bumps the run version itself, so re-read the prop instead of
    // checking `version`; a later prop change re-runs this watcher anyway.
    await stopLive()
    if (props.unavailableText) {
      statusText.value = props.unavailableText
    }
  },
  { immediate: true }
)

watch(() => props.rightBoxSelectionEnabled, enabled => {
  if (!enabled) {
    cancelRightPointerSelection()
  }
})

onMounted(() => {
  document.addEventListener('fullscreenchange', syncFullscreenState)
  document.addEventListener('webkitfullscreenchange', syncFullscreenState)
  if (playerWrapRef.value && typeof ResizeObserver !== 'undefined') {
    playerResizeObserver = new ResizeObserver(updateVideoDisplayRect)
    playerResizeObserver.observe(playerWrapRef.value)
  }
  updateVideoDisplayRect()
})

onBeforeUnmount(() => {
  nextRunVersion()
  document.removeEventListener('fullscreenchange', syncFullscreenState)
  document.removeEventListener('webkitfullscreenchange', syncFullscreenState)
  playerResizeObserver?.disconnect()
  playerResizeObserver = null
  clearSuppressContextMenuTimer()
  cancelRightPointerSelection()
  unbindActiveVideoElement()
  stopLive(false)
})

defineExpose({
  stopLive,
  switchLens: switchLensTo,
})

async function openPanel (version: number) {
  await cleanupLive(true, false)
  if (!isCurrent(version)) {
    return
  }
  resetState()
  loading.value = true
  statusText.value = '正在获取直播能力...'
  try {
    await loadCapacity()
    if (!isCurrent(version)) {
      return
    }
    if (sourceList.value.length === 0) {
      const handledByCapacityWait = await waitForDroneCapacity(version)
      if (!isCurrent(version)) {
        return
      }
      if (sourceList.value.length === 0) {
        if (!handledByCapacityWait) {
          statusText.value = '暂无可用直播源'
        }
        emitLiveState(false)
        return
      }
    }
    selectSource(sourceList.value[0])
    await nextTick()
    await startSelectedLive(version)
  } catch (error: any) {
    if (!isCurrent(version)) {
      return
    }
    statusText.value = '直播启动失败'
    emitLiveState(false)
    message.error(error?.message || String(error))
  } finally {
    loading.value = false
  }
}

async function loadCapacity () {
  const res = await getLiveCapacity({})
  if (res.code !== 0) {
    throw new Error(res.message || '获取直播能力失败')
  }
  sourceList.value = (res.data || [])
    .filter((item: any) => isTargetLiveSource(item))
    .map((item: any) => ({
      label: `${item.name || item.sn}-${item.sn}`,
      value: item.sn,
      more: getCameras(item)
    }))
    .filter((item: SelectOption) => item.more && item.more.length > 0)
}

function isTargetLiveSource (item: any) {
  if (!item?.sn || !props.targetSn) {
    return false
  }
  return item.sn === props.targetSn
}

async function waitForDroneCapacity (version: number) {
  if (props.sourceType !== 'drone') {
    return false
  }
  const waitingText = props.drcActive ? '已获取控制权，正在等待无人机直播源...' : '正在等待无人机直播源...'
  const emptyText = '飞行器已开机但未上报直播能力，暂无可用直播源'
  statusText.value = waitingText
  for (let index = 1; index <= DRONE_CAPACITY_RETRY_COUNT; index += 1) {
    await sleep(DRONE_CAPACITY_RETRY_INTERVAL)
    if (!isCurrent(version)) {
      return true
    }
    statusText.value = `${waitingText}(${index}/${DRONE_CAPACITY_RETRY_COUNT})`
    await loadCapacity()
    if (sourceList.value.length > 0) {
      return true
    }
  }
  statusText.value = emptyText
  return true
}

function getCameras (source: any) {
  return source.cameras_list || source.camerasList || []
}

function getVideos (camera: any) {
  return camera.videos_list || camera.videosList || []
}

function getSwitchVideoTypes (video: any) {
  return video.switch_video_types || video.switchVideoTypes || []
}

function selectSource (source: SelectOption) {
  sourceSelected.value = source.value
  cameraList.value = (source.more || []).map((camera: any) => ({
    label: String(camera.name || camera.camera_name || camera.cameraName || camera.index),
    value: String(camera.index),
    more: getVideos(camera)
  }))
  selectCamera(getDefaultCamera())
}

function getDefaultCamera () {
  if (props.sourceType !== 'drone') {
    return cameraList.value[0]
  }
  if (props.preferredCamera === 'auxiliary') {
    return cameraList.value.find(camera => camera.value === DRONE_AUXILIARY_CAMERA_INDEX) ||
      cameraList.value.find(camera => !DRONE_MAIN_CAMERA_INDEXES.includes(camera.value) && supportsDroneLens(camera)) ||
      cameraList.value.find(camera => !DRONE_MAIN_CAMERA_INDEXES.includes(camera.value))
  }
  if (props.preferredCamera && props.preferredCamera !== 'main') {
    return cameraList.value.find(camera => camera.value === props.preferredCamera)
  }
  return cameraList.value.find(camera => DRONE_MAIN_CAMERA_INDEXES.includes(camera.value)) ||
    cameraList.value.find(camera => camera.value !== DRONE_AUXILIARY_CAMERA_INDEX && supportsDroneLens(camera)) ||
    cameraList.value.find(camera => camera.value !== DRONE_AUXILIARY_CAMERA_INDEX) ||
    cameraList.value[0]
}

function supportsDroneLens (camera: SelectOption) {
  return (camera.more || []).some((video: any) => {
    const videoType = video.type || video.video_type || video.videoType
    const availableTypes = [videoType, ...getSwitchVideoTypes(video)].map(type => String(type))
    return DRONE_LENS_TYPES.some(lens => availableTypes.includes(lens))
  })
}

function selectCamera (camera?: SelectOption) {
  cameraSelected.value = camera?.value
  videoList.value = (camera?.more || []).map((video: any) => ({
    label: String(video.type || video.video_type || video.videoType || video.index),
    value: String(video.index),
    more: getSwitchVideoTypes(video)
  }))
  const firstVideo = videoList.value[0]
  videoSelected.value = firstVideo?.value
  lensList.value = Array.from(new Set([firstVideo?.label, ...(firstVideo?.more || [])].filter(Boolean) as string[]))
  lensSelected.value = firstVideo?.label || lensList.value[0]
  emitLensState()
}

async function onCameraChange (value: string) {
  const camera = cameraList.value.find(item => item.value === value)
  selectCamera(camera)
  if (liveState.value || activeVideoId.value) {
    await restartLive()
  }
}

async function restartLive () {
  const version = nextRunVersion()
  loading.value = true
  try {
    await cleanupLive(true, false)
    await startSelectedLive(version)
  } catch (error: any) {
    statusText.value = '直播启动失败'
    emitLiveState(false)
    message.error(error?.message || String(error))
  } finally {
    loading.value = false
  }
}

async function startSelectedLive (version: number) {
  if (!isCurrent(version)) {
    return
  }
  if (!sourceSelected.value || !cameraSelected.value) {
    throw new Error('暂无可用直播源')
  }

  const videoId = buildSelectedVideoId()
  lastStoppedVideoId = ''
  activeVideoId.value = videoId
  statusText.value = '正在启动推流...'
  const res = await startLivestream({
    video_id: videoId,
    url_type: 0,
    video_quality: claritySelected.value
  }, {
    skipErrorMessage: true
  })
  if (res.code !== 0) {
    if (isLiveAlreadyStartedResponse(res)) {
      await resumeExistingLive(videoId, version)
      return
    }
    activeVideoId.value = ''
    throw new Error(res.message || '启动直播失败')
  }
  if (!isCurrent(version)) {
    await stopLivestream({ video_id: videoId }).catch(error => console.error(error))
    activeVideoId.value = ''
    return
  }
  try {
    await joinAgora(res.data)
  } catch (error) {
    await cleanupLive(true)
    throw error
  }
  liveState.value = true
  statusText.value = '直播已启动，等待视频流...'
  emitLiveState(true)
  // M4TD may accept normal-0 but publish no video until a concrete lens is
  // selected.  Use the first advertised switchable lens automatically; this
  // is the same command as pressing the existing “切换” button and is a
  // no-op for cameras that only expose the normal lens.
  await autoSelectFirstLens()
}

async function autoSelectFirstLens () {
  if (!activeVideoId.value || lensList.value.length === 0 || !liveState.value) {
    return
  }
  const firstSwitchableLens = lensList.value.find((lens: string) => lens !== 'normal')
  if (!firstSwitchableLens || lensSelected.value === firstSwitchableLens) {
    return
  }
  lensSelected.value = firstSwitchableLens
  const res = await changeLivestreamLens({
    video_id: activeVideoId.value,
    video_type: firstSwitchableLens
  })
  if (res.code !== 0) {
    // Keep the stream usable if a device rejects the optional auto-switch;
    // the operator can still select another lens manually.
    console.warn('自动选择直播镜头失败', res.message)
    return
  }
  emitLensState()
}

async function resumeExistingLive (videoId: string, version: number) {
  statusText.value = '设备已在直播，正在恢复播放...'
  const res = await refreshAgoraToken({
    video_id: videoId,
    uid: 0
  })
  if (res.code !== 0) {
    throw new Error(res.message || '恢复直播失败')
  }
  if (!isCurrent(version)) {
    return
  }
  await joinAgora(res.data)
  liveState.value = true
  statusText.value = '直播已启动，等待视频流...'
  emitLiveState(true)
  await autoSelectFirstLens()
}

async function joinAgora (session: any) {
  await leaveAgora()
  if (!session?.app_id || !session?.channel || !session?.token) {
    throw new Error('Agora 播放参数不完整')
  }

  statusText.value = '正在连接声网频道...'
  agoraClient = AgoraRTC.createClient({ mode: 'live', codec: props.codec || 'vp8' })
  agoraClient.setClientRole('audience', { level: 2 })
  agoraClient.on('user-published', onUserPublished)
  agoraClient.on('user-unpublished', onUserUnpublished)
  agoraClient.on('token-privilege-will-expire', renewAgoraToken)
  agoraClient.on('token-privilege-did-expire', renewAgoraToken)
  agoraClient.on('connection-state-change', (curState: string) => {
    if (curState !== 'CONNECTED') {
      statusText.value = `声网连接状态: ${curState}`
    }
  })
  await agoraClient.join(session.app_id, session.channel, session.token, getAgoraJoinUid(session))
  await subscribeExistingRemoteUsers()
}

async function onUserPublished (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') {
  if (!agoraClient) {
    return
  }
  if (mediaType === 'video') {
    if (!user.videoTrack) {
      await agoraClient.subscribe(user, mediaType)
    }
    if (playerRef.value && user.videoTrack) {
      playerRef.value.innerHTML = ''
      unbindSeiTrack()
      activeVideoTrack = user.videoTrack
      activeVideoUid = user.uid
      bindSeiTrack(user.videoTrack)
      user.videoTrack.play(playerRef.value, { fit: 'cover' })
      videoReady.value = true
      statusText.value = '直播中'
      startLatencyTimer()
      await nextTick()
      bindActiveVideoElement()
      await restoreAliyunRelay()
    }
    return
  }

  if (!user.audioTrack) {
    await agoraClient.subscribe(user, mediaType)
  }
  if (user.audioTrack) {
    user.audioTrack.play()
  }
}

function onUserUnpublished (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') {
  if (mediaType === 'video') {
    videoReady.value = false
    stopLatencyTimer()
  }
  statusText.value = '直播流已断开'
}

async function subscribeExistingRemoteUsers () {
  if (!agoraClient) {
    return
  }
  const remoteUsers = [...agoraClient.remoteUsers]
  for (const user of remoteUsers) {
    if (user.hasVideo) {
      await onUserPublished(user, 'video').catch(error => console.error(error))
    }
    if (user.hasAudio) {
      await onUserPublished(user, 'audio').catch(error => console.error(error))
    }
  }
}

async function renewAgoraToken () {
  if (!agoraClient || !activeVideoId.value) {
    return
  }
  const res = await refreshAgoraToken({
    video_id: activeVideoId.value,
    uid: getActiveAudienceUid()
  })
  if (res.code !== 0) {
    message.error(res.message || '刷新 Agora Token 失败')
    return
  }
  await agoraClient.renewToken(res.data.token)
}

async function updateQuality () {
  if (!activeVideoId.value) {
    return
  }
  const res = await setLivestreamQuality({
    video_id: activeVideoId.value,
    video_quality: claritySelected.value
  })
  if (res.code === 0) {
    message.success('清晰度已更新')
  }
}

async function selectCameraByValue (value: string) {
  if (value === cameraSelected.value) {
    return
  }
  cameraSelected.value = value
  await onCameraChange(value)
}

/**
 * Lens chips switch immediately; a rejected switch rolls the chip back so the
 * bar never claims a lens the aircraft is not streaming.
 */
async function selectLens (lens: string) {
  if (lens === lensSelected.value || lensSwitching.value) {
    return
  }
  const previous = lensSelected.value
  lensSwitching.value = lens
  try {
    if (!(await switchLensTo(lens))) {
      lensSelected.value = previous
      emitLensState()
    }
  } catch (error) {
    lensSelected.value = previous
    emitLensState()
    throw error
  } finally {
    lensSwitching.value = ''
  }
}

async function switchLensTo (lens: string) {
  if (!activeVideoId.value || !liveState.value) {
    message.warning('直播尚未就绪，无法切换镜头')
    return false
  }
  if (!lensList.value.includes(lens)) {
    message.warning(`当前相机不支持 ${lens} 镜头`)
    return false
  }
  lensSelected.value = lens
  const res = await changeLivestreamLens({
    video_id: activeVideoId.value,
    video_type: lens
  })
  if (res.code === 0) {
    message.success('镜头切换指令已下发')
    emitLensState()
    return true
  }
  return false
}

async function stopCurrentLive () {
  if (props.closeOnStop) {
    emit('request-close')
    return
  }
  await stopLive()
}

async function toggleAliyunRelay () {
  try {
    await toggleAliyunRelayInternal()
  } catch (error: any) {
    message.error(error?.message || '阿里云直播操作失败')
  }
}

async function stopLive (emitState = true) {
  if (stopLivePromise) {
    await stopLivePromise
    return
  }
  nextRunVersion()
  const stopVideoId = stoppableVideoId.value
  if (stopVideoId && stopVideoId === lastStoppedVideoId) {
    if (emitState) {
      emitLiveState(false)
    }
    return
  }
  stopLivePromise = cleanupLive(true, emitState, stopVideoId)
    .finally(() => {
      stopLivePromise = null
    })
  await stopLivePromise
}

async function cleanupLive (stopDevice: boolean, emitState = true, stopVideoId = stoppableVideoId.value) {
  await exitFullscreen()
  if (stopDevice) {
    await stopAliyunRelayForSourceShutdown()
  }
  await leaveAgora()
  if (stopDevice && stopVideoId) {
    lastStoppedVideoId = stopVideoId
    activeVideoId.value = ''
    await stopLivestream({ video_id: stopVideoId }).catch(error => console.error(error))
  }
  liveState.value = false
  videoReady.value = false
  statusText.value = '直播已停止'
  if (emitState) {
    emitLiveState(false)
  }
}

async function toggleFullscreen () {
  if (fullscreenActive.value) {
    await exitFullscreen()
    return
  }
  await enterFullscreen()
}

async function enterFullscreen () {
  const wrap = playerWrapRef.value
  if (!wrap) {
    return
  }
  fallbackFullscreen = false
  const requestFullscreen = wrap.requestFullscreen || (wrap as any).webkitRequestFullscreen
  if (!requestFullscreen) {
    fallbackFullscreen = true
    fullscreenActive.value = true
    return
  }
  try {
    await requestFullscreen.call(wrap)
    fullscreenActive.value = true
  } catch (error) {
    fallbackFullscreen = true
    fullscreenActive.value = true
  }
}

async function exitFullscreen () {
  if (fallbackFullscreen) {
    fallbackFullscreen = false
    fullscreenActive.value = false
    return
  }
  const fullscreenElement = document.fullscreenElement || (document as any).webkitFullscreenElement
  if (fullscreenElement) {
    const exit = document.exitFullscreen || (document as any).webkitExitFullscreen
    if (exit) {
      await Promise.resolve(exit.call(document)).catch((error: any) => console.error(error))
    }
  }
  fullscreenActive.value = false
}

function syncFullscreenState () {
  const fullscreenElement = document.fullscreenElement || (document as any).webkitFullscreenElement
  if (fullscreenElement) {
    fullscreenActive.value = fullscreenElement === playerWrapRef.value
    return
  }
  if (!fallbackFullscreen) {
    fullscreenActive.value = false
  }
}

async function leaveAgora () {
  if (!agoraClient) {
    clearPlayer()
    return
  }
  agoraClient.removeAllListeners()
  await agoraClient.leave().catch(error => console.error(error))
  agoraClient = null
  clearPlayer()
}

function clearPlayer () {
  stopLatencyTimer()
  unbindActiveVideoElement()
  if (playerRef.value) {
    playerRef.value.innerHTML = ''
  }
  updateVideoDisplayRect()
}

function bindActiveVideoElement () {
  const video = playerRef.value?.querySelector('video') || null
  if (video === activeVideoElement) {
    updateVideoDisplayRect()
    return
  }
  unbindActiveVideoElement()
  activeVideoElement = video
  activeVideoElement?.addEventListener('loadedmetadata', updateVideoDisplayRect)
  activeVideoElement?.addEventListener('resize', updateVideoDisplayRect)
  updateVideoDisplayRect()
}

function unbindActiveVideoElement () {
  activeVideoElement?.removeEventListener('loadedmetadata', updateVideoDisplayRect)
  activeVideoElement?.removeEventListener('resize', updateVideoDisplayRect)
  activeVideoElement = null
}

function updateVideoDisplayRect () {
  const wrap = playerWrapRef.value
  if (!wrap) {
    videoDisplayRect.value = calculateCoverRect(0, 0, 16, 9)
    return
  }
  const sourceWidth = activeVideoElement?.videoWidth || 16
  const sourceHeight = activeVideoElement?.videoHeight || 9
  videoDisplayRect.value = calculateCoverRect(
    wrap.clientWidth,
    wrap.clientHeight,
    sourceWidth,
    sourceHeight
  )
}

function startLatencyTimer () {
  clearLatencyTimer()
  updateLatency()
  latencyTimer = window.setInterval(updateLatency, 1000)
}

function clearLatencyTimer () {
  if (latencyTimer !== undefined) {
    window.clearInterval(latencyTimer)
    latencyTimer = undefined
  }
}

function stopLatencyTimer () {
  clearLatencyTimer()
  unbindSeiTrack()
  activeVideoTrack = null
  activeVideoUid = null
  latencyMs.value = null
}

function updateLatency () {
  const delay = getLatencyValue(activeVideoTrack?.getStats()) ?? getLatencyValue(getClientRemoteVideoStats())
  if (delay === null) {
    latencyMs.value = null
    return
  }
  latencyMs.value = delay
}

function getClientRemoteVideoStats () {
  if (!agoraClient || activeVideoUid === null) {
    return undefined
  }
  return agoraClient.getRemoteVideoStats()[String(activeVideoUid)]
}

function getLatencyValue (stats?: Record<string, any>) {
  if (!stats) {
    return null
  }
  const latencyFields = ['receiveDelay', 'end2EndDelay', 'transportDelay']
  for (const field of latencyFields) {
    const value = Number(stats[field])
    if (Number.isFinite(value) && value >= 0) {
      return Math.round(value)
    }
  }
  return null
}

function resetState () {
  sourceList.value = []
  cameraList.value = []
  videoList.value = []
  lensList.value = []
  sourceSelected.value = undefined
  cameraSelected.value = undefined
  videoSelected.value = undefined
  lensSelected.value = undefined
  claritySelected.value = 0
  liveState.value = false
  videoReady.value = false
  activeVideoId.value = ''
  latencyMs.value = null
  fullscreenActive.value = false
  statusText.value = '等待直播启动'
  emitLensState()
}

function getAgoraJoinUid (session: any) {
  return session && Object.prototype.hasOwnProperty.call(session, 'uid') ? session.uid : null
}

function getActiveAudienceUid () {
  const uid = agoraClient?.uid
  return typeof uid === 'number' ? uid : 0
}

function buildSelectedVideoId () {
  if (!sourceSelected.value || !cameraSelected.value) {
    return ''
  }
  return `${sourceSelected.value}/${cameraSelected.value}/${videoSelected.value || 'normal-0'}`
}

function isLiveAlreadyStartedResponse (res: { code: number, message?: string }) {
  return res.code === LIVE_ALREADY_STARTED_CODE || res.message === 'The camera has started live streaming.'
}

function nextRunVersion () {
  runVersion += 1
  return runVersion
}

function isCurrent (version: number) {
  return props.active && !!props.targetSn && version === runVersion
}

function sleep (ms: number) {
  return new Promise(resolve => window.setTimeout(resolve, ms))
}

function emitLiveState (liveStateValue: boolean) {
  emit('live-state-change', liveStateValue)
}

function emitLensState () {
  emit('lens-state-change', {
    selected: lensSelected.value || '',
    options: [...lensList.value],
  })
}

function pickAimPoint (event: MouseEvent) {
  if (!videoReady.value || (event.target as HTMLElement | null)?.closest('button, .ant-select')) {
    return
  }
  const wrap = playerWrapRef.value
  if (!wrap) {
    return
  }
  const rect = wrap.getBoundingClientRect()
  if (!rect.width || !rect.height) {
    return
  }
  const displayRect = videoDisplayRect.value
  const x = (event.clientX - rect.left - displayRect.left) / displayRect.width
  const y = (event.clientY - rect.top - displayRect.top) / displayRect.height
  if (x < 0 || x > 1 || y < 0 || y > 1) {
    return
  }
  emit('aim-point', { x, y })
}

function pickContextPoint (event: MouseEvent) {
  if (!props.contextPointEnabled) {
    return
  }
  event.preventDefault()
  const wrap = playerWrapRef.value
  if (!wrap) {
    return
  }
  const rect = wrap.getBoundingClientRect()
  if (!rect.width || !rect.height) {
    return
  }
  const containerX = Math.min(rect.width, Math.max(0, event.clientX - rect.left))
  const containerY = Math.min(rect.height, Math.max(0, event.clientY - rect.top))
  const displayRect = { ...videoDisplayRect.value }
  const normalizedVideoX = displayRect.width > 0 ? (containerX - displayRect.left) / displayRect.width : 0
  const normalizedVideoY = displayRect.height > 0 ? (containerY - displayRect.top) / displayRect.height : 0
  const payload: LiveContextPoint = {
    containerX,
    containerY,
    videoX: normalizedVideoX * displayRect.sourceWidth,
    videoY: normalizedVideoY * displayRect.sourceHeight,
    cameraIndex: cameraSelected.value || '',
    lens: lensSelected.value || '',
    videoId: activeVideoId.value,
    latencyMs: latencyMs.value,
    videoReady: videoReady.value,
    videoRect: displayRect,
  }
  emit('context-point', payload)
}

function bindSeiTrack (track: IRemoteVideoTrack) {
  if (props.codec !== 'h264') {
    return
  }
  activeSeiTrack = track
  activeSeiTrack.on('sei-received', handleSeiReceived)
}

function unbindSeiTrack () {
  if (activeSeiTrack) {
    activeSeiTrack.off('sei-received', handleSeiReceived)
    activeSeiTrack = null
  }
  emit('sei-data', null)
}

function handleSeiReceived (data: Uint8Array) {
  emit('sei-data', data instanceof Uint8Array ? data : new Uint8Array(data))
}

function getContainerPoint (event: PointerEvent) {
  const wrap = playerWrapRef.value
  if (!wrap) {
    return null
  }
  const rect = wrap.getBoundingClientRect()
  if (!rect.width || !rect.height) {
    return null
  }
  return {
    x: Math.min(rect.width, Math.max(0, event.clientX - rect.left)),
    y: Math.min(rect.height, Math.max(0, event.clientY - rect.top)),
  }
}

function handleRightPointerDown (event: PointerEvent) {
  if (
    event.button !== 2 ||
    !props.rightBoxSelectionEnabled ||
    !videoReady.value ||
    isPlayerInteractiveTarget(event.target)
  ) {
    return
  }
  const point = getContainerPoint(event)
  if (!point) {
    return
  }
  clearSuppressContextMenuTimer()
  rightPointerSelection.value = {
    pointerId: event.pointerId,
    startX: point.x,
    startY: point.y,
    currentX: point.x,
    currentY: point.y,
    dragging: false,
  }
  try {
    playerWrapRef.value?.setPointerCapture?.(event.pointerId)
  } catch (_error) {
    // Pointer capture is optional; window-local pointer events still work.
  }
}

function handleRightPointerMove (event: PointerEvent) {
  const selection = rightPointerSelection.value
  if (!selection || selection.pointerId !== event.pointerId) {
    return
  }
  const point = getContainerPoint(event)
  if (!point) {
    return
  }
  selection.currentX = point.x
  selection.currentY = point.y
  selection.dragging = isRightBoxDrag(selection.startX, selection.startY, point.x, point.y)
  if (selection.dragging) {
    event.preventDefault()
  }
}

function handleRightPointerUp (event: PointerEvent) {
  const selection = rightPointerSelection.value
  if (!selection || selection.pointerId !== event.pointerId || event.button !== 2) {
    return
  }
  const point = getContainerPoint(event)
  if (point) {
    selection.currentX = point.x
    selection.currentY = point.y
    selection.dragging = isRightBoxDrag(selection.startX, selection.startY, point.x, point.y)
  }
  releaseRightPointerCapture(event.pointerId)
  rightPointerSelection.value = null
  suppressNextContextMenu()
  event.preventDefault()

  if (!selection.dragging) {
    pickContextPoint(event as unknown as MouseEvent)
    return
  }
  event.stopPropagation()
  const normalized = normalizeVideoRect(
    selection.startX,
    selection.startY,
    selection.currentX,
    selection.currentY,
    videoDisplayRect.value,
    0.01
  )
  if (!normalized) {
    emit('right-box-selection-invalid')
    return
  }
  emit('right-box-select', normalized)
}

function cancelRightPointerSelection (event?: PointerEvent) {
  const selection = rightPointerSelection.value
  if (event && selection?.pointerId !== event.pointerId) {
    return
  }
  if (selection) {
    releaseRightPointerCapture(selection.pointerId)
  }
  rightPointerSelection.value = null
}

function handlePlayerContextMenu (event: MouseEvent) {
  if (isPlayerInteractiveTarget(event.target)) {
    event.preventDefault()
    return
  }
  if (rightPointerSelection.value || consumeSuppressedContextMenu()) {
    event.preventDefault()
    event.stopPropagation()
    return
  }
  pickContextPoint(event)
}

function releaseRightPointerCapture (pointerId: number) {
  const wrap = playerWrapRef.value
  if (!wrap?.hasPointerCapture?.(pointerId)) {
    return
  }
  try {
    wrap.releasePointerCapture(pointerId)
  } catch (_error) {
    // The browser may have released capture after pointercancel already.
  }
}

function isPlayerInteractiveTarget (target: EventTarget | null) {
  return target instanceof HTMLElement && !!target.closest('button, a, input, textarea, select, [contenteditable="true"], .ant-select')
}

function suppressNextContextMenu () {
  clearSuppressContextMenuTimer()
  suppressContextMenuTimer = window.setTimeout(() => {
    suppressContextMenuTimer = undefined
  }, 500)
}

function consumeSuppressedContextMenu () {
  if (suppressContextMenuTimer === undefined) {
    return false
  }
  clearSuppressContextMenuTimer()
  return true
}

function clearSuppressContextMenuTimer () {
  if (suppressContextMenuTimer !== undefined) {
    window.clearTimeout(suppressContextMenuTimer)
    suppressContextMenuTimer = undefined
  }
}

function normalizedRectStyle (rect: PayloadInteractionRect) {
  const displayRect = videoDisplayRect.value
  return {
    left: `${displayRect.left + rect.x * displayRect.width}px`,
    top: `${displayRect.top + rect.y * displayRect.height}px`,
    width: `${rect.width * displayRect.width}px`,
    height: `${rect.height * displayRect.height}px`,
  }
}

</script>

<style lang="scss" scoped>
.agora-inline {
  margin: 8px 5px 10px;
  padding: 8px;
  background: rgba(17, 24, 39, 0.9);
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 2px;
}

.agora-inline__header,
.agora-inline__controls,
.agora-inline__lenses {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.agora-inline__header {
  justify-content: space-between;
  margin-bottom: 6px;
}

.agora-inline__title {
  font-weight: 600;
}

.agora-inline__status {
  color: #cbd5e1;
}

.agora-inline__label {
  color: #8fa0ac;
  font-size: 11px;
  font-weight: 600;
}

.agora-inline__relay-status {
  color: #e0b064;
  font-size: 11px;
}

/* Control bar chrome shared by the cockpit and the TSA live windows: dark
 * glass groups, segmented chips, ghost buttons, green active state. */
.agora-inline__group {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  background: rgba(0, 0, 0, 0.42);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 7px;
}

.agora-inline__group-label {
  display: inline-flex;
  align-items: center;
  padding: 0 4px 0 6px;
  color: #8fa0ac;
  font-size: 13px;
}

.agora-inline__chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 24px;
  gap: 4px;
  padding: 0 10px;
  color: #c9d4dd;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 5px;
  cursor: pointer;
  transition: color 120ms ease, background 120ms ease, border-color 120ms ease;
}

.agora-inline__chip:hover:not(:disabled) {
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
}

.agora-inline__chip--active {
  color: #04130d;
  background: #00df89;
  border-color: #43f0ad;
}

.agora-inline__chip:disabled {
  color: #5f6c76;
  cursor: not-allowed;
}

.agora-inline__chip--active:disabled {
  color: rgba(4, 19, 13, 0.7);
  background: rgba(0, 224, 136, 0.55);
  border-color: transparent;
}

.agora-inline__btn {
  display: inline-flex;
  align-items: center;
  height: 28px;
  gap: 6px;
  padding: 0 11px;
  color: #eef3f7;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 7px;
  cursor: pointer;
  transition: color 120ms ease, background 120ms ease, border-color 120ms ease;
}

.agora-inline__btn :deep(.i-icon) {
  display: inline-flex;
  font-size: 14px;
}

.agora-inline__btn:hover:not(:disabled) {
  color: #fff;
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(0, 224, 136, 0.5);
}

.agora-inline__btn:disabled {
  color: #5f6c76;
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.06);
  cursor: not-allowed;
}

.agora-inline__btn--stop {
  color: #ffb4b0;
  border-color: rgba(255, 107, 107, 0.4);
}

.agora-inline__btn--stop:hover:not(:disabled) {
  color: #fff;
  background: rgba(229, 50, 45, 0.3);
  border-color: #ff8a8a;
}

.agora-inline__stop-mark {
  width: 9px;
  height: 9px;
  background: currentColor;
  border-radius: 2px;
}

.agora-inline__btn--relay-active {
  color: #fff;
  background: rgba(30, 142, 245, 0.28);
  border-color: rgba(30, 142, 245, 0.65);
}

.agora-inline__btn--relay-active:hover:not(:disabled) {
  background: rgba(30, 142, 245, 0.4);
  border-color: #7cc4ff;
}

.agora-inline__divider {
  width: 1px;
  height: 18px;
  background: rgba(255, 255, 255, 0.12);
}

.agora-inline__select :deep(.ant-select-selector) {
  height: 28px !important;
  color: #e6edf2 !important;
  background: rgba(255, 255, 255, 0.07) !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  border-radius: 7px !important;
  box-shadow: none !important;
}

.agora-inline__select :deep(.ant-select-selection-item),
.agora-inline__select :deep(.ant-select-selection-placeholder) {
  color: #e6edf2;
  font-size: 11px;
  font-weight: 600;
  line-height: 26px !important;
}

.agora-inline__select :deep(.ant-select-arrow) {
  color: #9fb0bb;
}

.agora-inline__select.ant-select-focused :deep(.ant-select-selector) {
  border-color: rgba(0, 224, 136, 0.6) !important;
}

.agora-inline__select.ant-select-disabled :deep(.ant-select-selector) {
  color: #5f6c76 !important;
  background: rgba(255, 255, 255, 0.03) !important;
  border-color: rgba(255, 255, 255, 0.06) !important;
}

.agora-inline__select.ant-select-disabled :deep(.ant-select-selection-item) {
  color: #5f6c76;
}

.agora-inline__player-wrap {
  position: relative;
  width: 100%;
  height: 220px;
  margin-bottom: 8px;
  background: #05070a;
  overflow: hidden;
}

.agora-inline__player-wrap--fullscreen,
.agora-inline__player-wrap:fullscreen,
.agora-inline__player-wrap:-webkit-full-screen {
  position: fixed;
  inset: 0;
  z-index: 10000;
  width: 100vw;
  height: 100vh;
  margin: 0;
  background: #000;
}

.agora-inline__player {
  width: 100%;
  height: 100%;
}

.agora-inline__right-selection {
  position: absolute;
  z-index: 12;
  box-sizing: border-box;
  background: rgba(0, 224, 136, 0.12);
  border: 2px dashed #00e08a;
  box-shadow: 0 0 14px rgba(0, 224, 136, 0.48);
  pointer-events: none;
}

.agora-inline__placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #e5e7eb;
  pointer-events: none;
}

.agora-inline__aim-picker {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 18px;
  color: #eefcf7;
  background:
    linear-gradient(rgba(0, 224, 136, 0.72), rgba(0, 224, 136, 0.72)) center / 1px 32px no-repeat,
    linear-gradient(90deg, rgba(0, 224, 136, 0.72), rgba(0, 224, 136, 0.72)) center / 32px 1px no-repeat,
    rgba(0, 0, 0, 0.04);
  border: 0;
  cursor: crosshair;
}

.agora-inline__aim-hint {
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  background: rgba(5, 12, 16, 0.82);
  border: 1px solid rgba(0, 224, 136, 0.45);
  border-radius: 4px;
}

.agora-inline__fullscreen {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  color: #f8fafc;
  font-size: 16px;
  line-height: 1;
  background: rgba(0, 0, 0, 0.68);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 2px;
  cursor: pointer;
}

.agora-inline__fullscreen:hover {
  background: rgba(15, 23, 42, 0.86);
}

.agora-inline__latency {
  position: absolute;
  right: 8px;
  bottom: 8px;
  padding: 2px 6px;
  min-width: 48px;
  color: #f8fafc;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  background: rgba(0, 0, 0, 0.68);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 2px;
  pointer-events: none;
}

.agora-inline__select {
  width: 116px;
}

.agora-inline__select--clarity {
  width: 104px;
}

.agora-inline__lenses {
  margin-top: 8px;
}
</style>

<style lang="scss">
/* The clarity / camera dropdown renders in <body>, outside the scoped tree. */
.agora-inline-dropdown {
  padding: 4px;
  background: #12171c;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
}

.agora-inline-dropdown .ant-select-item {
  color: #c9d4dd;
  font-size: 11px;
  border-radius: 5px;
}

.agora-inline-dropdown .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
}

.agora-inline-dropdown .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
  color: #04130d;
  font-weight: 600;
  background: #00df89;
}
</style>
